/**
 * Non-component helpers for `AlertBanner`, kept out of the `.tsx` so that module
 * exports only components and stays a Vite Fast Refresh boundary.
 */

/**
 * Classes for a CTA that sits in an `AlertBanner`'s `action` slot: a white
 * button outlined and lettered in the banner's tone. Reads the custom properties
 * the banner root sets (`--banner-action-ink`, `--banner-line`, `--banner-hover`),
 * so the one class string works for all four tones, both appearances and both
 * themes. `AlertBannerAction` applies it; a ready-made button that renders its
 * own `Button` (e.g. `BillingPortalButton`) takes it via `className`.
 */
export const ALERT_BANNER_ACTION_CLASS =
  'h-8 border border-[var(--banner-line)] bg-card px-3 font-bold text-[var(--banner-action-ink)] hover:bg-[var(--banner-hover)] hover:text-[var(--banner-action-ink)]';

/** The quieter sibling of `ALERT_BANNER_ACTION_CLASS` — a second, lower-priority CTA. */
export const ALERT_BANNER_SUBTLE_ACTION_CLASS =
  'h-8 border border-[var(--banner-subtle-line)] bg-transparent px-3 font-bold text-[var(--banner-ink)] hover:bg-[var(--banner-ghost-hover)] hover:text-[var(--banner-ink)]';

/**
 * Splits a message into its first sentence and the rest, so a banner can bold
 * the lead (`title`) and keep the existing copy word-for-word. A sentence ends
 * at `.`, `?` or `!` followed by whitespace; a message with a single sentence
 * comes back as `{ lead: message, rest: '' }`.
 */
export function splitLeadSentence(message: string): { lead: string; rest: string } {
  const match = /^(.+?[.?!])\s+(.+)$/s.exec(message.trim());
  if (!match) return { lead: message.trim(), rest: '' };
  return { lead: match[1], rest: match[2] };
}
